"use client";

// One list per role, cut into headed sections — Creator Scripts, Onboarding,
// Admin — but ONLY for a role whose list spans more than one of them. A creator
// has three pages and they are all Creator Scripts; a heading over the whole of
// a list says nothing the list does not already say, so a creator and a brand
// get the flat list they had.
//
// ⚠️ THIS IS NOT THE OLD GROUPED NAV COMING BACK. That one (Creator / Workflow /
// AM / Client-Brand / Admin / Labs, with live badge counts) died because it
// headed lists of one or two entries with pages that have since been deleted
// outright — the Labs pages, the review and oversight queues, the entity and
// reporting dashboards. What is here is three headings over twelve entries, and
// it renders nothing at all below that threshold.
//
// ⚠️ AND THREE BADGES, WHICH IS NOT THAT EITHER. `badges` is passed in by the
// layout, keyed by href: Brands & access, counting decisions a person owes —
// grants about to lapse and vendor identities with evidence behind them
// (lib/server/brand-admin.ts) — and Reacher writes, the admin's mark when the
// write key pool is Behind or a write has errored. The old nav died of badges
// that counted whatever a query returned; these count work somebody can finish
// (add a key, retry a write), and each disappears when it is done. A count that
// cannot reach zero is a count nobody reads, so a third badge needs that
// argument made again — and the Client Inbox's made it (app/layout.tsx): each
// client message waiting is answered, or marked, and goes. The fourth,
// Settings' red `!` while the TikTok session is dead, makes it too: one sign-in
// through the TikTok browser and it goes. So does the Operations Inbox's: a
// card left to do today is a task somebody finishes, and it goes when they do.
// That one is not the layout's: it is what is left of the task chip's day
// (components/task-chip.tsx), so the badge and the chip are one reading.
//
// The lists themselves live in lib/nav-config.ts, because "/" routes each role
// to its own first nav entry and home-route.ts cannot import a client module —
// and the sections are cut there too (navSectionsFor), so the shape is testable
// without a DOM and the rule "the array's order IS the grouping" has one home.
//
// ⚠️ AND BECAUSE THE LIST IS THE PERMISSION. What this component renders is the
// same array the page gates read (canOpen), so a link missing from a sidebar is
// not a link missing from a sidebar — it is a page that will not open. Adding an
// entry here grants the page; there is no second place that also has to agree.

import { IntentLink } from "@/components/intent-link";
import { usePathname } from "next/navigation";
import { Layers, X } from "lucide-react";
import clsx from "clsx";
import { usePersona } from "@/components/persona-context";
import { useInboxDay } from "@/components/task-chip";
import { withLens } from "@/lib/lens-url";
import { useMobileNav } from "@/components/mobile-nav-context";
import { navSectionsFor, isNavActive, OPS_INBOX } from "@/lib/nav-config";
import { inboxBadgeOf } from "@/lib/ops-inbox-page";
import type { NavItem } from "@/lib/nav-config";
import { APP_VERSION as VERSION } from "@/lib/ironside-version";
import { ORG_NAME, PRODUCT_NAME } from "@/lib/product";

const P = "rgb(var(--c-accent))";

/** A count of work waiting, and the sentence saying what it is made of. */
export interface NavBadge {
  count: number;
  title: string;
  /**
   * What the badge shows instead of the count — the Reacher writes mark's `!`
   * (Behind: add a key), which is a state to fix rather than a number of things.
   */
  label?: string;
  /** Red for something broken (the Reacher writes mark); the accent otherwise. */
  tone?: "accent" | "red";
}

function DNavLink({ item, active, badge, lensKey }: { item: NavItem; active: boolean; badge?: NavBadge; lensKey: string | null }) {
  const { setOpen: setMobileOpen } = useMobileNav();
  const Icon = item.icon;
  return (
    // Prefetched on hover or focus, not on view: see intent-link.tsx.
    <IntentLink
      data-analytics-feature={item.href}
      // ⚠️ AN ADMIN'S LINK CARRIES THE LENS ON SCREEN (`?as=`). Without it the
      // page lands unnamed and is renamed by a second navigation
      // (components/lens-url-sync.tsx); with it the entry names its lens from
      // the start, and the server renders it from the URL, not the cookie.
      href={lensKey ? withLens({ pathname: item.href, params: [], hash: "" }, lensKey) : item.href}
      onClick={() => setMobileOpen(false)}
      className={clsx(
        "flex items-center gap-2.5 px-3 py-2 rounded-md text-[12.5px] font-medium transition-colors",
        active ? "text-fg" : "text-text-muted hover:text-text hover:bg-card"
      )}
      style={active ? { background: "rgb(var(--c-accent) / 0.1333)" } : {}}
    >
      {/* shrink-0: a long label gives way, never the icon. */}
      <Icon size={14} className="shrink-0" style={active ? { color: P } : {}} />
      {/* ⚠️ A LABEL THAT DOESN'T FIT ENDS IN AN ELLIPSIS, and its hover says it
          whole. No skill has an item of its own: what each does is on the
          Skills page. */}
      <span className="min-w-0 truncate" title={item.label} data-testid={`nav-label-${item.href}`}>
        {item.label}
      </span>
      {badge && badge.count > 0 && (
        <span
          // ml-auto so it sits at the right edge whatever the label's length,
          // and text-onfill because it is text on a solid accent in both themes.
          className="ml-auto shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-semibold tabular-nums text-onfill"
          style={{ background: badge.tone === "red" ? "rgb(var(--c-red))" : P }}
          title={badge.title}
          data-testid={`nav-badge-${item.href}`}
        >
          {/* ⚠️ A BARE NUMBER IS NOT A SENTENCE. Read aloud, the link would be
              "Brands and access, 2" — a count of nothing in particular. The
              digit is the sighted reader's, the sentence beside it is the
              screen reader's, and `title` gives the same words on hover. */}
          <span aria-hidden="true">{badge.label ?? badge.count}</span>
          <span className="sr-only">{badge.title}</span>
        </span>
      )}
    </IntentLink>
  );
}

export function AppSidebar({ badges }: { badges?: Record<string, NavBadge> }) {
  const path = usePathname();
  const { persona, lensKey } = usePersona();
  const { open: mobileOpen, setOpen: setMobileOpen } = useMobileNav();
  const day = useInboxDay();
  const inboxBadge = day ? inboxBadgeOf(day) : null;

  // ⚠️ ONE SECTION MEANS NO HEADING, not a heading of one. See the note at the
  // top: this is the whole of the "creators and brands see no headers" rule.
  const sections = navSectionsFor(persona.role);
  const headed = sections.length > 1;

  return (
    <>
      {/* Mobile-only backdrop — tap to close. Sidebar is off-canvas by
          default below the md breakpoint (see aside classes below); this
          only ever renders while a hamburger tap has opened it. */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-scrim/60 z-40 md:hidden"
          onClick={() => setMobileOpen(false)}
          aria-hidden="true"
        />
      )}
      <aside
        className={clsx(
          "fixed inset-y-0 left-0 w-56 flex flex-col bg-chrome border-r border-hairline z-50",
          "transition-transform duration-200 md:translate-x-0",
          mobileOpen ? "translate-x-0" : "-translate-x-full",
        )}
      >
      {/* Logo */}
      <div className="px-5 py-5 border-b border-hairline flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 rounded-md flex items-center justify-center" style={{ background: P }}>
            <Layers size={14} className="text-fg" />
          </div>
          <div>
            <div className="text-[13px] font-semibold text-fg leading-tight">{ORG_NAME}</div>
            <div className="text-[9px] font-mono uppercase tracking-widest font-semibold" style={{ color: P }}>{PRODUCT_NAME}</div>
          </div>
        </div>
        <button
          onClick={() => setMobileOpen(false)}
          aria-label="Close navigation"
          className="md:hidden text-text-dim hover:text-text-muted transition-colors"
        >
          <X size={16} />
        </button>
      </div>

      <nav className="flex-1 px-3 py-4 overflow-y-auto">
        {sections.map((section) => (
          /* The gap between sections is the heading's margin when there is one,
             and this class when there is not — so an unheaded sidebar is the
             single run of links it was before, not three runs with air. */
          /* Keyed on the first link, not the category: navSectionsFor emits a
             category TWICE for a list that interleaves them (deliberately — see
             its note), and a repeated key is React's worst failure mode. */
          <div key={section.items[0].href} className={clsx("space-y-0.5", headed ? "mb-5 last:mb-0" : "")}>
            {headed && (
              <h2 className="px-3 pb-1.5 pt-0.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-text-dim">
                {section.category}
              </h2>
            )}
            {section.items.map((item) => (
              <DNavLink
                key={item.href}
                item={item}
                active={isNavActive(item.href, path)}
                badge={item.href === OPS_INBOX.href ? (inboxBadge ?? undefined) : badges?.[item.href]}
                lensKey={lensKey}
              />
            ))}
          </div>
        ))}
      </nav>

      {/* Bottom */}
      <div className="px-3 py-4 border-t border-hairline">
        <div className="px-3 pb-1">
          <span className="text-[10px] text-text-dim font-mono">{VERSION}</span>
        </div>
      </div>
      </aside>
    </>
  );
}
